<template>
    <NuxtLayout name="malt-base-layout">
        <VJoyFunnel
            :layout-width="vJoyFunnelWidth"
            hide-shapes
            :class="{
                'funnel-layout--medium-up-to-small-desktop': layoutWidth === 'large_responsive',
                'vertical-center': verticalCenter,
            }"
        >
            <template #funnel-header>
                <VJoyFunnelHeader>
                    <template v-if="backUrl" #funnel-header-left>
                        <NuxtLink v-slot="{href, navigate}" :to="{path: backUrl}" :external="externalBackUrl" custom>
                            <VJoyButton
                                icon="arrow-left"
                                variant="ghost"
                                :href="href"
                                data-testid="back-button"
                                class="advanced-search-funnel__button"
                                @click="navigate"
                            >
                                {{ $t('general.back') }}
                            </VJoyButton>
                        </NuxtLink>
                    </template>
                    <template #funnel-header-right>
                        <slot name="header-right">
                            <VJoyIconButton
                                data-testid="advanced-search-close-button"
                                :label="$t('general.close')"
                                icon="cross"
                                size="medium"
                                variant="ghost"
                                @click="handleClose"
                            />
                        </slot>
                    </template>
                </VJoyFunnelHeader>
            </template>
            <slot />
            <VJoyFunnelFooter v-if="hasFooterSlot">
                <template v-if="slots['footer-left']" #footer-left>
                    <slot name="footer-left" />
                </template>
                <template v-if="slots['footer-right']" #footer-right>
                    <slot name="footer-right" />
                </template>
            </VJoyFunnelFooter>
        </VJoyFunnel>
    </NuxtLayout>
</template>
<script setup lang="ts">
    import {computed, useSlots} from 'vue';
    import {type TJoyFunnelLayoutWidths, VJoyFunnel, VJoyFunnelHeader, VJoyButton, VJoyFunnelFooter, VJoyIconButton} from '@maltjoy/core-vue';

    export interface LayoutProps {
        // 'large_responsive' means "use a large layout when the screen is larger than 'small_desktop'"
        layoutWidth?: TJoyFunnelLayoutWidths | 'large_responsive';
        backUrl?: string;
        verticalCenter?: boolean;
        externalBackUrl?: boolean;
    }

    export type Slots = {
        default?: any;
        'header-right'?: any;
        'footer-left'?: any;
        'footer-right'?: any;
    };

    defineSlots<Slots>();

    const slots: Slots = useSlots();

    const hasFooterSlot = computed(() => slots['footer-left'] !== undefined || slots['footer-right'] !== undefined);

    const props = withDefaults(defineProps<LayoutProps>(), {
        layoutWidth: 'medium',
        externalBackUrl: false,
    });

    const emits = defineEmits<{
        close: [];
    }>();

    const vJoyFunnelWidth = computed<TJoyFunnelLayoutWidths>(() => (props.layoutWidth === 'large_responsive' ? 'large' : props.layoutWidth));

    function handleClose() {
        emits('close');
    }
</script>

<style scoped lang="scss">
    @use '~/common/scss/media-queries' as asmq;

    .vertical-center {
        :deep(.joy-funnel__content) {
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
        }
    }

    .funnel-layout {
        &--medium-up-to-small-desktop {
            // we override the large funnel width with the medium with to fake a medium funnel using media queries
            @include asmq.up_to_small_desktop() {
                --funnel-content-width-large: var(--funnel-content-width-medium);
            }
        }
    }

    :deep(.joy-template .joy-template-slot-title) {
        margin: 0;
    }

    :deep(.joy-funnel__scrollable) {
        background-color: var(--joy-color-neutral-10);
        padding-top: var(--joy-core-spacing-9);
    }

    :deep(.joy-funnel--no-border .joy-funnel__header) {
        background-color: var(--joy-color-neutral-0);
    }

    :deep(.joy-header) {
        padding: var(--joy-core-spacing-3);
    }

    :deep(.joy-header__left .joy-button_medium) {
        padding: 0 var(--joy-core-spacing-3);
    }

    :deep(.joy-funnel__content) {
        padding: 0 var(--funnel-content-lateral-padding) var(--joy-core-spacing-10);
    }
</style>
